import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, Share2, Flame, Send, Bell } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import DealCard from "../components/DealCard";
import AdSlot from "../components/AdSlot";
import PriceAlertModal from "../components/PriceAlertModal";
import PriceHistorySection from "../components/PriceHistorySection";
import { api, fileUrl, STORE_LABEL, STORE_COLOR, priceFmt, dealTitle } from "../lib/api";
import Countdown from "../components/Countdown";
import Seo, { JsonLd, schemas } from "../components/Seo";

export default function DealDetail() {
  const { id } = useParams();
  const [copied, setCopied] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);

  // Hydrate from prerender bootstrap when present (matches this id)
  const initialDeal = (typeof window !== "undefined" && window.__BDD_INITIAL__?.route === "deal" && window.__BDD_INITIAL__?.deal?.id === id)
    ? window.__BDD_INITIAL__.deal
    : undefined;

  const { data: deal, isLoading } = useQuery({
    queryKey: ["deal", id],
    queryFn: () => api.get(`/deals/${id}`).then((r) => r.data),
    retry: false,
    initialData: initialDeal,
  });

  const { data: tgData } = useQuery({
    queryKey: ["tg-subs"],
    queryFn: () => api.get("/telegram-subscribers").then((r) => r.data),
    staleTime: 5 * 60_000,
  });
  const tgSubs = tgData?.count || 0;

  const { data: relatedRaw } = useQuery({
    queryKey: ["related", deal?.category, id],
    enabled: !!deal,
    queryFn: () =>
      api
        .get("/deals", { params: { category: deal.category, limit: 8 } })
        .then((r) => r.data.items),
  });
  const related = (relatedRaw || []).filter((x) => x.id !== id).slice(0, 4);

  // Internal linking: pull up to 3 recent blog articles for the same category bucket
  const { data: relatedArticles } = useQuery({
    queryKey: ["related-articles", deal?.category],
    enabled: !!deal,
    queryFn: () => api.get("/articles", { params: { limit: 12 } }).then((r) => r.data.items || []),
  });
  const articleMatches = React.useMemo(() => {
    if (!relatedArticles || !deal) return [];
    const cat = (deal.category || "").toLowerCase();
    const title = (deal.title || "").toLowerCase();
    const keywords = title.split(/\s+/).filter((w) => w.length >= 4).slice(0, 4);
    const scored = relatedArticles
      .map((a) => {
        const hay = ((a.title || "") + " " + (a.excerpt || "")).toLowerCase();
        let score = 0;
        if (cat && hay.includes(cat)) score += 3;
        for (const k of keywords) if (hay.includes(k)) score += 1;
        return { a, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score);
    const picks = scored.length >= 2 ? scored.map((x) => x.a) : relatedArticles;
    return picks.slice(0, 3);
  }, [relatedArticles, deal]);

  const loading = isLoading;
  const title = dealTitle(deal);

  const trackClick = () => {
    api.post(`/deals/${id}/click`).catch(() => {});
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (e) {
        // user cancelled share — fall through to clipboard
      }
    }
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-10">
            <div className="aspect-square rounded-2xl shimmer" />
            <div className="space-y-4">
              <div className="h-8 rounded shimmer" />
              <div className="h-6 rounded shimmer w-2/3" />
              <div className="h-32 rounded shimmer" />
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <h1 className="font-display font-black text-3xl text-white mb-3">
            Offerta non trovata
          </h1>
          <p className="text-zinc-400 mb-8">
            Potrebbe essere scaduta o rimossa. Sfoglia le offerte disponibili.
          </p>
          <Link to="/" data-testid="back-home" className="inline-flex items-center gap-2 bg-[#FBD51C] text-black font-bold px-5 py-3 rounded-lg">
            <ArrowLeft size={16} /> Torna alle offerte
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {deal && (
        <>
          <Seo
            title={title}
            description={
              deal.analisi_breve?.slice(0, 200) ||
              deal.description?.slice(0, 200) ||
              `Offerta ${title}${
                deal.discount_percent ? ` con sconto del ${deal.discount_percent}%` : ""
              }${
                deal.price_current
                  ? ` a €${deal.price_current.toFixed(2).replace(".", ",")}`
                  : ""
              }. Acquista ora su ${STORE_LABEL[deal.store] || deal.store}.`
            }
            path={`/offerta/${deal.id}`}
            image={`https://dailybrickdeals.com/api/og/deal/${deal.id}.png`}
            type="product"
          />
          <JsonLd data={schemas.product(deal)} />
          <JsonLd data={schemas.faqPage(deal.faq)} />
          <JsonLd
            data={schemas.breadcrumb([
              { name: "Home", url: "/" },
              { name: STORE_LABEL[deal.store] || "Offerte", url: `/?store=${deal.store}` },
              { name: title, url: `/offerta/${deal.id}` },
            ])}
          />
        </>
      )}
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link
          to="/"
          data-testid="back-link"
          className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-6"
        >
          <ArrowLeft size={14} /> Tutte le offerte
        </Link>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* image */}
          <div className="lg:col-span-7">
            <div className="relative aspect-square rounded-3xl overflow-hidden bg-zinc-900 border border-zinc-800 brick-pattern" data-testid="deal-image">
              {deal.image_url ? (
                <img
                  src={fileUrl(deal.image_url)}
                  alt={title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : null}
              {deal.discount_percent ? (
                <div className="absolute top-5 right-5 bg-rose-600 text-white text-lg font-black px-3 py-2 rounded-lg">
                  -{deal.discount_percent}%
                </div>
              ) : null}
              {deal.hot ? (
                <div className="absolute top-5 left-5 inline-flex items-center gap-1.5 bg-[#FBD51C] text-black text-xs font-black uppercase tracking-[0.2em] px-3 py-1.5 rounded-md">
                  <Flame size={12} /> Hot
                </div>
              ) : null}
            </div>
          </div>

          {/* info */}
          <div className="lg:col-span-5">
            <div
              className="font-mono-bdd text-xs uppercase tracking-[0.3em] mb-3"
              style={{ color: STORE_COLOR[deal.store] || "#FBD51C" }}
            >
              {STORE_LABEL[deal.store] || "Store"} · {deal.category}
            </div>
            <h1
              data-testid="deal-title"
              className="font-display font-black text-3xl lg:text-4xl text-white leading-tight tracking-tight"
            >
              {title}
            </h1>

            <div className="mt-6 p-5 rounded-2xl bg-zinc-900 border border-zinc-800">
              <div className="flex items-end gap-3">
                {deal.price_current ? (
                  <div className="font-display font-black text-4xl text-gold-grad" data-testid="deal-price">
                    {priceFmt(deal.price_current)}
                  </div>
                ) : (
                  <div className="font-display font-black text-2xl text-white">
                    Prezzo nel sito
                  </div>
                )}
                {deal.price_original && deal.price_original > (deal.price_current || 0) ? (
                  <div className="text-zinc-500 line-through pb-2">
                    {priceFmt(deal.price_original)}
                  </div>
                ) : null}
              </div>
              {deal.price_original && deal.price_current && deal.price_original > deal.price_current ? (
                <div className="text-sm text-rose-400 mt-2 font-bold">
                  Risparmi {priceFmt(deal.price_original - deal.price_current)}
                </div>
              ) : null}

              {deal.expires_at && (
                <div className="mt-4 p-3 bg-[#0d162d]/60 rounded-lg border border-[#1f2c4a]">
                  <Countdown to={deal.expires_at} label="Offerta scade tra" />
                </div>
              )}

              <a
                href={deal.affiliate_url}
                target="_blank"
                rel="noopener noreferrer sponsored"
                onClick={trackClick}
                data-testid="deal-cta"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#FBD51C] hover:bg-[#F5C518] text-black font-black text-sm uppercase tracking-wider px-5 py-4 rounded-xl transition-colors"
              >
                Vai all&apos;offerta su {STORE_LABEL[deal.store]} <ExternalLink size={16} />
              </a>
              <div className="text-[10px] text-zinc-600 mt-3 font-mono-bdd uppercase tracking-wider text-center">
                Link affiliato · sostieni il canale
              </div>

              {deal.price_current > 0 && (
                <button
                  type="button"
                  onClick={() => setAlertOpen(true)}
                  data-testid="deal-price-alert-btn"
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-[#FBD51C]/40 text-white font-bold text-sm px-5 py-3 rounded-xl transition-colors"
                >
                  <Bell size={15} className="text-[#FBD51C]" />
                  Avvisami se il prezzo scende
                </button>
              )}

              {tgSubs > 0 && (
                <a
                  href="https://t.me/BrickDailyDeals"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="deal-tg-proof"
                  className="mt-4 w-full flex items-center justify-center gap-3 bg-[#229ED9]/10 hover:bg-[#229ED9]/20 border border-[#229ED9]/30 rounded-xl px-4 py-3 transition-colors group"
                >
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#229ED9] opacity-60"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#229ED9]"></span>
                  </span>
                  <div className="text-xs sm:text-sm text-[#86d2f5] text-left leading-tight">
                    <div className="font-bold">
                      {tgSubs.toLocaleString("it-IT")} persone
                    </div>
                    <div className="opacity-80">
                      ricevono queste offerte in tempo reale
                    </div>
                  </div>
                  <Send size={16} className="text-[#229ED9] ml-auto group-hover:translate-x-0.5 transition-transform" />
                </a>
              )}
            </div>

            {deal.analisi_breve ? (
              <div className="mt-6 p-5 rounded-2xl bg-gradient-to-br from-[#0d162d]/80 to-zinc-900/80 border border-[#1f2c4a]" data-testid="deal-analisi">
                <div className="font-mono-bdd text-[10px] uppercase tracking-[0.3em] text-[#FBD51C] mb-2">
                  La nostra analisi
                </div>
                <p className="text-zinc-200 text-sm leading-relaxed">
                  {deal.analisi_breve}
                </p>
              </div>
            ) : null}

            <PriceHistorySection deal={deal} />

            {deal.perche_comprarlo ? (
              <section className="mt-8" data-testid="deal-perche">
                <div className="font-mono-bdd text-[10px] uppercase tracking-[0.3em] text-[#FBD51C] mb-2">
                  Perché merita
                </div>
                <h2 className="font-display font-black text-xl text-white mb-3">Perché comprarlo</h2>
                <p className="text-zinc-300 text-sm leading-relaxed">{deal.perche_comprarlo}</p>
              </section>
            ) : null}

            {deal.dettagli_set ? (
              <section className="mt-8" data-testid="deal-dettagli">
                <div className="font-mono-bdd text-[10px] uppercase tracking-[0.3em] text-[#FBD51C] mb-2">
                  Il set nel dettaglio
                </div>
                <h2 className="font-display font-black text-xl text-white mb-3">Dettagli del set</h2>
                <p className="text-zinc-300 text-sm leading-relaxed">{deal.dettagli_set}</p>
              </section>
            ) : null}

            {deal.contesto_prezzo ? (
              <section className="mt-8" data-testid="deal-contesto">
                <div className="font-mono-bdd text-[10px] uppercase tracking-[0.3em] text-[#FBD51C] mb-2">
                  Analisi prezzo
                </div>
                <h2 className="font-display font-black text-xl text-white mb-3">
                  Conviene davvero?
                </h2>
                <p className="text-zinc-300 text-sm leading-relaxed">{deal.contesto_prezzo}</p>
              </section>
            ) : null}

            {Array.isArray(deal.faq) && deal.faq.length > 0 ? (
              <section className="mt-8" data-testid="deal-faq">
                <div className="font-mono-bdd text-[10px] uppercase tracking-[0.3em] text-[#FBD51C] mb-2">
                  Domande frequenti
                </div>
                <h2 className="font-display font-black text-xl text-white mb-4">FAQ</h2>
                <div className="space-y-3">
                  {deal.faq.map((item, i) => (
                    <details
                      key={i}
                      className="group p-4 rounded-xl bg-zinc-900/60 border border-zinc-800 hover:border-[#FBD51C]/30 transition-colors"
                      data-testid={`deal-faq-item-${i}`}
                    >
                      <summary className="cursor-pointer list-none flex items-start justify-between gap-3">
                        <span className="font-display font-bold text-white text-sm leading-snug">
                          {item.q}
                        </span>
                        <span className="mt-0.5 shrink-0 text-[#FBD51C] font-mono-bdd text-xs group-open:rotate-45 transition-transform">＋</span>
                      </summary>
                      <p className="mt-3 text-zinc-400 text-sm leading-relaxed">{item.a}</p>
                    </details>
                  ))}
                </div>
              </section>
            ) : null}

            {deal.description ? (
              <div className="mt-8">
                <div className="font-mono-bdd text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
                  Dettagli dal canale
                </div>
                <p
                  data-testid="deal-description"
                  className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap"
                >
                  {deal.description}
                </p>
              </div>
            ) : null}

            <div className="mt-6 flex gap-3">
              <button
                onClick={share}
                data-testid="deal-share"
                className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white text-sm font-bold px-4 py-2.5 rounded-lg"
              >
                <Share2 size={14} /> {copied ? "Copiato!" : "Condividi"}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-12">
          <AdSlot id="below-detail" size="leaderboard" />
        </div>

        {articleMatches.length > 0 && (
          <div className="mt-14" data-testid="related-articles">
            <div className="font-mono-bdd text-[11px] uppercase tracking-[0.3em] text-[#FBD51C] mb-2">
              Approfondisci
            </div>
            <h2 className="font-display font-black text-2xl text-white mb-6">
              Dal blog di BrickDailyDeals
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {articleMatches.map((a) => (
                <Link
                  key={a.id || a.slug}
                  to={`/blog/${a.slug}`}
                  data-testid={`related-article-${a.slug}`}
                  className="group block p-5 rounded-2xl bg-zinc-900/70 border border-zinc-800 hover:border-[#FBD51C]/40 hover:bg-zinc-900 transition-colors"
                >
                  <div className="font-mono-bdd text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
                    Articolo
                  </div>
                  <div className="font-display font-bold text-base text-white leading-snug group-hover:text-[#FBD51C] transition-colors">
                    {a.title}
                  </div>
                  {a.excerpt ? (
                    <p className="mt-2 text-xs text-zinc-400 leading-relaxed line-clamp-3">
                      {a.excerpt}
                    </p>
                  ) : null}
                  <div className="mt-3 text-[11px] font-mono-bdd uppercase tracking-wider text-[#FBD51C] inline-flex items-center gap-1">
                    Leggi <ExternalLink size={11} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {related.length > 0 && (
          <div className="mt-12">
            <div className="font-mono-bdd text-[11px] uppercase tracking-[0.3em] text-[#FBD51C] mb-2">
              Offerte simili
            </div>
            <h2 className="font-display font-black text-2xl text-white mb-6">
              Potrebbero piacerti
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {related.map((d, i) => (
                <DealCard key={d.id} deal={d} idx={i} />
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
      {alertOpen && deal && (
        <PriceAlertModal deal={deal} onClose={() => setAlertOpen(false)} />
      )}
    </div>
  );
}
